import React, { JSX } from 'react';
import { Field, FormikErrors, FormikTouched } from 'formik';
import { useTranslation } from 'next-i18next';
import clsx from 'clsx';
import { fillYearsOptions, getMonthName } from '@helpers/utils/date-utils';
import { SellerInformationContext } from '@context/seller-information.context';
import { useContextSelector } from 'use-context-selector';
import { usePreferences } from '@context/preferences.context';
import Icon from '@components/atoms/icon/icon';
import useWindowSize from '@hooks/use-window-size';
import { SellerFormValueType as FormValueType } from '@interfaces/models/seller-info/seller-information';
import styles from '../seller-information-form/seller-information-form.module.scss';
import SellerFormTextInput from './seller-form-text-input';

type PersonalInformationProps = {
  values: FormValueType;
  errors?: FormikErrors<FormValueType>;
  touched?: FormikTouched<FormValueType>;
  dirty?: boolean;
};

const createNumberArray = (n) => {
  return Array.from({ length: n }, (_, i) => i + 1);
};

const PersonalInformation: React.FC<PersonalInformationProps> = (props): JSX.Element => {
  const { values, errors, touched, dirty } = props;
  const dateAndAgeErrors = useContextSelector(SellerInformationContext, (value) => value.dateAndAgeErrors);
  const { t } = useTranslation('get-paid');
  const { isWiderThanMd } = useWindowSize();
  const { locale } = usePreferences();

  return (
    <div className={clsx(styles.formGroup, styles['formGroup--formGroupFlexContainer'])}>
      <div
        className={clsx(
          styles.formTitle,
          styles['formTitle--infoTitle'],
          isWiderThanMd ? 'vc-text-H24' : 'vc-text-H20',
          'vc-text-bold',
        )}
      >
        {t('GET_PAID.TAX_INFORMATION_FORM.PERSONAL_INFO_SUBTITLE', 'Personal Information')}
      </div>
      {/* Firstname */}
      <div
        className={clsx('vc-formUnit', styles['vc-formUnit'], {
          [styles.error + ' error']: errors?.firstname && touched?.firstname,
        })}
      >
        <SellerFormTextInput
          name="firstname"
          label={t('GET_PAID.PERSONAL_INFORMATION_FORM.LABEL.FIRSTNAME')}
          value={values.firstname}
          placeholder={t('GET_PAID.PERSONAL_INFORMATION_FORM.PLACEHOLDER.FIRSTNAME')}
          required
          dirty={dirty}
          touched={touched}
          maxLength={100}
          t={t}
        />
      </div>
      {/* Lastname */}
      <div
        className={clsx('vc-formUnit', styles['vc-formUnit'], {
          [styles.error + ' error']: errors?.lastname && touched?.lastname,
        })}
      >
        <SellerFormTextInput
          name="lastname"
          label={t('GET_PAID.PERSONAL_INFORMATION_FORM.LABEL.LASTNAME')}
          value={values.lastname}
          placeholder={t('GET_PAID.PERSONAL_INFORMATION_FORM.PLACEHOLDER.LASTNAME')}
          required
          dirty={dirty}
          touched={touched}
          maxLength={100}
          t={t}
        />
      </div>
      {/* Date of Birth */}
      <div
        className={clsx('vc-formUnit', styles['vc-formUnit'], {
          [styles.error + ' error']: dateAndAgeErrors?.dateError || dateAndAgeErrors?.ageError,
        })}
      >
        <div className={styles.dateOfBirth}>
          {/* Day */}
          <div
            className={`
              vc-formUnit
              vc-formUnit--select
              ${styles['vc-formUnit']}
              ${styles['vc-formUnit--select']}
              ${errors?.day && touched?.day ? styles.error : ''}
            `}
          >
            <Field
              as="select"
              name="day"
              aria-labelledby="label-day"
              className={clsx('vc-inputText', styles['vc-inputText'], {
                [styles['vc-inputText--touched']]: dirty && touched?.day,
              })}
              value={values.day}
              required
            >
              <option
                value=""
                disabled
              >
                {t('GET_PAID.PERSONAL_INFORMATION_FORM.LABEL.DAY')}
              </option>
              {createNumberArray(31).map((i) => (
                <option
                  key={i}
                  value={i.toString().padStart(2, '0')}
                >
                  {i}
                </option>
              ))}
            </Field>
          </div>

          {/* Month */}
          <div
            className={`
              vc-formUnit
              vc-formUnit--select
              ${styles['vc-formUnit']}
              ${styles['vc-formUnit--select']}
              ${errors?.month && touched?.month ? styles.error : ''}
            `}
          >
            <Field
              as="select"
              name="month"
              aria-labelledby="label-month"
              className={clsx('vc-inputText', styles['vc-inputText'], {
                [styles['vc-inputText--touched']]: dirty && touched?.month,
              })}
              value={values.month}
              required
            >
              <option
                value=""
                disabled
              >
                {t('GET_PAID.PERSONAL_INFORMATION_FORM.LABEL.MONTH')}
              </option>
              {createNumberArray(12).map((i: number) => (
                <option
                  key={i}
                  value={i.toString().padStart(2, '0')}
                >
                  {getMonthName(i, locale)}
                </option>
              ))}
            </Field>
          </div>

          {/* Year */}
          <div
            className={`
              vc-formUnit
              vc-formUnit--select
              ${styles['vc-formUnit']}
              ${styles['vc-formUnit--select']}
              ${errors?.year && touched?.year ? styles.error : ''}
            `}
          >
            <Field
              as="select"
              name="year"
              aria-labelledby="label-year"
              className={clsx('vc-inputText', styles['vc-inputText'], {
                [styles['vc-inputText--touched']]: dirty && touched?.year,
              })}
              value={values.year}
              required
            >
              <option
                value=""
                disabled
              >
                {t('GET_PAID.PERSONAL_INFORMATION_FORM.LABEL.YEAR')}
              </option>
              {fillYearsOptions().map((i) => (
                <option
                  key={i}
                  value={i}
                >
                  {i}
                </option>
              ))}
            </Field>
          </div>
        </div>

        <label
          id="label-dob"
          className={`vc-inputText-label ${styles['vc-inputText-label']} ${styles.required}`}
        >
          {t('GET_PAID.PERSONAL_INFORMATION_FORM.LABEL.DATE_OF_BIRTH')}
        </label>

        {(dateAndAgeErrors.dateError || dateAndAgeErrors.ageError) && (
          <div className={`vc-inputText-errorMsg vc-text-xs ${styles.labelErrorContainer}`}>
            <span className={styles.labelErrorContainer__icon}>
              <Icon
                name="warning-20"
                data-cy="error_info_icon"
                ariaHidden
              />
            </span>
            {dateAndAgeErrors.ageError ? (
              <span>{t('GET_PAID.TAX_INFORMATION_FORM.ERROR.INVALID_DOB', 'You must be over 18 years old.')}</span>
            ) : (
              dateAndAgeErrors.dateError && (
                <span>{t('GET_PAID.TAX_INFORMATION_FORM.ERROR.INVALID_DATE', 'Date is invalid')}</span>
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PersonalInformation;
